import { useEffect, useState } from "react";
import { Gift, Plus, Copy, Trash2, Tag, Check } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const PLAN_LABEL = { pro_standard: "Pro", pro_plus: "Pro Plus" };

export default function AdminCodes() {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, used: 0, unused: 0 });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  // create form
  const [plan, setPlan] = useState("pro_standard");
  const [days, setDays] = useState(30);
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await api.get("/admin/codes", { params });
      setCodes(r.data.codes || []);
      setStats(r.data.stats || { total: 0, used: 0, unused: 0 });
    } catch (e) {
      toast.error("Could not load codes");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/admin/codes/generate", { plan, days: Number(days), quantity: Number(quantity), note });
      toast.success(`Created ${r.data.count} code${r.data.count !== 1 ? "s" : ""}`);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setCreating(false); }
  };

  const remove = async (code) => {
    if (!confirm(`Delete code ${code}? Only unused codes can be deleted.`)) return;
    try {
      await api.delete(`/admin/codes/${code}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-5xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Admin</p>
        <h1 className="font-display text-4xl font-black mt-1 flex items-center gap-2">
          <Gift className="w-8 h-8" strokeWidth={2.5}/> Gift codes
        </h1>
        <p className="text-sm font-medium text-slate-600 mt-2">Generate codes for promos, influencers, support compensation, or contests. One-time use per code.</p>
      </header>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="nb-card p-4"><div className="text-xs font-bold uppercase tracking-widest">Total</div><div className="font-display text-3xl font-black mt-1">{stats.total}</div></div>
        <div className="nb-card p-4 bg-amber-100"><div className="text-xs font-bold uppercase tracking-widest">Unused</div><div className="font-display text-3xl font-black mt-1">{stats.unused}</div></div>
        <div className="nb-card p-4 bg-emerald-200"><div className="text-xs font-bold uppercase tracking-widest">Redeemed</div><div className="font-display text-3xl font-black mt-1">{stats.used}</div></div>
      </div>

      <section className="nb-card p-6 mb-6" data-testid="codes-create">
        <h2 className="font-display text-2xl font-bold flex items-center gap-2"><Plus className="w-5 h-5" strokeWidth={2.5}/> Generate codes</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold">Plan</label>
            <select data-testid="codes-plan" value={plan} onChange={(e) => setPlan(e.target.value)} className="nb-input mt-1">
              <option value="pro_standard">Pro</option>
              <option value="pro_plus">Pro Plus</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold">Days</label>
            <input data-testid="codes-days" type="number" min={1} max={365} value={days} onChange={(e) => setDays(e.target.value)} className="nb-input mt-1"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold">Quantity</label>
            <input data-testid="codes-quantity" type="number" min={1} max={100} value={quantity} onChange={(e) => setQuantity(e.target.value)} className="nb-input mt-1"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold">Note (optional)</label>
            <input data-testid="codes-note" type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Spring promo" className="nb-input mt-1"/>
          </div>
        </div>
        <button onClick={create} disabled={creating} data-testid="codes-generate-btn" className="nb-btn-primary mt-4 inline-flex items-center gap-2">
          {creating ? "Creating…" : <><Tag className="w-4 h-4" strokeWidth={2.5}/> Generate</>}
        </button>
      </section>

      <div className="flex gap-3 mb-4 flex-wrap">
        <button onClick={() => setFilter("")} className={`nb-tag text-xs ${filter === "" ? "bg-slate-900 text-white" : "bg-white"}`}>All</button>
        <button onClick={() => setFilter("unredeemed")} className={`nb-tag text-xs ${filter === "unredeemed" ? "bg-slate-900 text-white" : "bg-white"}`}>Unused</button>
        <button onClick={() => setFilter("redeemed")} className={`nb-tag text-xs ${filter === "redeemed" ? "bg-slate-900 text-white" : "bg-white"}`}>Redeemed</button>
      </div>

      {loading ? (
        <div className="nb-card p-12 text-center">Loading…</div>
      ) : codes.length === 0 ? (
        <div className="nb-card p-12 text-center">
          <p className="font-display text-xl font-bold">No codes yet.</p>
          <p className="text-sm text-slate-600 mt-2">Generate one above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {codes.map((c) => (
            <div key={c.code} className="nb-card p-3 flex items-center flex-wrap gap-3" data-testid={`code-${c.code}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-mono font-bold tracking-wider text-lg">{c.code}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {PLAN_LABEL[c.plan]} · {c.days} days
                  {c.note && <> · <em>{c.note}</em></>}
                </div>
              </div>
              {c.redeemed_at ? (
                <div className="text-xs text-slate-700">
                  <span className="nb-tag bg-emerald-200 text-xs">Used</span>
                  <div className="mt-1">by {c.redeemed_by_email}</div>
                  <div className="text-slate-500">{new Date(c.redeemed_at).toLocaleDateString()}</div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => copy(c.code)} data-testid={`copy-${c.code}`} className="nb-btn-secondary text-xs py-2 px-3 inline-flex items-center gap-1">
                    {copied === c.code ? <><Check className="w-3 h-3" strokeWidth={2.5}/> Copied</> : <><Copy className="w-3 h-3" strokeWidth={2.5}/> Copy</>}
                  </button>
                  <button onClick={() => remove(c.code)} className="nb-btn-danger text-xs py-2 px-3 inline-flex items-center gap-1">
                    <Trash2 className="w-3 h-3" strokeWidth={2.5}/>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
